<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <v-row>
          <v-col md="6" cols="12">
            <div class="label mb-2">Name</div>
            <v-text-field
              v-model="form.name"
              :rules="[validationRules.required]"
              :error="!!form.errors && !!form.errors.name"
              :error-messages="form.errors.name"
              solo
              flat
              outlined
              dense
            />
          </v-col>
          <v-col md="6" cols="12">
            <div class="label mb-2">Name (Chinese)</div>
            <v-text-field
              v-model="form.name_cn"
              :error="!!form.errors && !!form.errors.name_cn"
              :error-messages="form.errors.name_cn"
              solo
              flat
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col md="6" cols="12">
            <div class="label mb-2">Curriculum</div>
            <v-select
              v-model="form.curriculum_id"
              :items="curriculums"
              item-text="name"
              item-value="id"
              :rules="[validationRules.required]"
              :error="!!form.errors && !!form.errors.curriculum_id"
              :error-messages="form.errors.curriculum_id"
              :loading="isCurriculumListLoading"
              solo
              flat
              outlined
              dense
              placeholder="Select curriculum"
              @change="changeCurriculum"
            />
          </v-col>
          <v-col md="3" cols="12">
            <div class="label mb-2">Jumlah Pertemuan</div>
            <v-text-field
              v-model="form.class_per_week"
              :rules="[validationRules.required]"
              :error="!!form.errors && !!form.errors.class_per_week"
              :error-messages="form.errors.class_per_week"
              type="number"
              suffix="X per minggu"
              solo
              flat
              outlined
              dense
              placeholder="Jumlah pertemuan"
            />
          </v-col>
          <v-col md="3" cols="12">
            <v-switch
              v-model="form.is_templated"
              inset
              hide-details
              label="Using Templated Lessons?"
              class="ma-0"
            />
          </v-col>
        </v-row>
        <v-fade-transition>
          <v-row v-show="!form.is_templated">
            <v-col md="3" cols="12">
              <div class="label mb-2">Total Terms</div>
              <v-text-field
                v-model="form.total_terms"
                :error="!!form.errors && !!form.errors.total_terms"
                :error-messages="form.errors.total_terms"
                type="number"
                solo
                flat
                outlined
                dense
                placeholder="Total class terms"
              />
            </v-col>
            <v-col md="3" cols="12">
              <div class="label mb-2">Total Months</div>
              <v-text-field
                v-model="form.total_months"
                :error="!!form.errors && !!form.errors.total_months"
                :error-messages="form.errors.total_months"
                type="number"
                solo
                flat
                outlined
                dense
                placeholder="Total class months"
              />
            </v-col>
            <v-col md="3" cols="12">
              <div class="label mb-2">Total Sessions</div>
              <v-text-field
                v-model="form.total_sessions"
                :error="!!form.errors.total_sessions"
                :error-messages="form.errors.total_sessions"
                type="number"
                solo
                flat
                outlined
                dense
                placeholder="Total class sessions"
              />
            </v-col>
          </v-row>
        </v-fade-transition>
        <v-fade-transition>
          <v-card v-show="form.is_templated" flat>
            <v-card-title class="px-0">
              <v-row align="center">
                <v-col cols="12" md="6">Lesson Materials</v-col>
                <v-col v-if="!isReadonly" cols="12" md="6" class="text-right">
                  <lesson-form-dialog
                    v-model="addLessonDialog"
                    mode="add"
                    @submit="addLesson"
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        depressed
                        color="success"
                        :block="$vuetify.breakpoint.mobile"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left>mdi-plus</v-icon>Add Lesson
                      </v-btn>
                    </template>
                  </lesson-form-dialog>
                </v-col>
              </v-row>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="px-0">
              <v-data-table
                v-show="form.is_templated"
                :headers="headers"
                :items="lessons"
                multi-sort
              >
                <template #top>
                  <v-container fluid>
                    <v-row align="center">
                      <v-col cols="12" md="2">
                        <div class="label mb-2">Term</div>
                        <v-select
                          v-model="filters.term"
                          :items="terms"
                          outlined
                          solo
                          flat
                          background-color="white"
                          label="Term"
                          hide-details
                          dense
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <div class="label mb-2">Month</div>
                        <v-select
                          v-model="filters.month"
                          :items="months"
                          outlined
                          solo
                          flat
                          background-color="white"
                          label="Month"
                          hide-details
                          dense
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <div class="label mb-2">Session</div>
                        <v-select
                          v-model="filters.session"
                          :items="sessions"
                          outlined
                          solo
                          flat
                          background-color="white"
                          label="Session"
                          hide-details
                          dense
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="label mb-2">Name</div>
                        <v-text-field
                          v-model="filters.name"
                          outlined
                          solo
                          flat
                          background-color="white"
                          label="Name"
                          hide-details
                          dense
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="label mb-2">Lesson Category</div>
                        <v-select
                          v-model="filters.lesson_category_id"
                          :items="lessonCategories"
                          :item-text="
                            (item) => bilingualText(item, 'name', 'name_cn')
                          "
                          item-value="id"
                          outlined
                          solo
                          flat
                          background-color="white"
                          label="Category"
                          hide-details
                          dense
                          clearable
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </template>
                <template #[`item.category`]="{ item }">
                  {{ getLessonCategory(item.lesson_category_id) }}
                </template>
                <template #[`item.attachments`]="{ item }">
                  <ul v-if="item.attachment_file_url !== null">
                    <li
                      v-for="(
                        attachment_url, j
                      ) in item.attachment_file_url.split(',')"
                      :key="j"
                    >
                      <a :href="attachment_url" target="_blank">
                        {{ filenameFromUrl(attachment_url) }}
                      </a>
                    </li>
                  </ul>
                </template>
                <template #[`item.action`]="{ item }">
                  <div class="text-right">
                    <v-btn
                      depressed
                      color="error"
                      small
                      :disabled="isReadonly"
                      @click="removeLesson(item)"
                    >
                      <v-icon left>mdi-delete-forever</v-icon>Delete
                    </v-btn>
                    <v-btn
                      depressed
                      color="warning"
                      small
                      :disabled="isReadonly"
                      @click="showEditLessonDialog(item)"
                    >
                      <v-icon left>mdi-pencil</v-icon>Edit
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-fade-transition>
        <div class="label mb-2">Status</div>
        <v-select
          v-model="form.status"
          :items="statusOptions"
          :rules="[validationRules.required]"
          :error="!!form.errors && !!form.errors.status"
          :error-messages="form.errors.status"
          solo
          flat
          outlined
          dense
          placeholder="Select status"
        />
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
    <lesson-form-dialog
      v-if="editLessonDialog.show"
      v-model="editLessonDialog.show"
      mode="edit"
      :lesson="editLessonDialog.item"
      @submit="editLesson"
    ></lesson-form-dialog>
  </v-card>
</template>
<script>
import collect from 'collect.js'
import constant from '@/constant'
import { mapGetters, mapActions } from 'vuex'
import LessonFormDialog from '@components/Lesson/FormDialog'
export default {
  name: 'LessonPlanForm',
  components: { LessonFormDialog },
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    lessonPlan: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      form: this.$inertia.form({
        name: null,
        name_cn: null,
        description: null,
        rank: null,
        status: null,
        curriculum_id: null,
        class_per_week: null,
        minutes_per_class: null,
        is_templated: false,
        total_terms: null,
        total_months: null,
        total_sessions: null,
        lessons: [],
      }),
      headers: [
        { text: 'Term', value: 'term' },
        { text: 'Month', value: 'month' },
        { text: 'Session', value: 'session' },
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Name (Chinese)', value: 'name_cn', sortable: false },
        { text: 'Category', value: 'category', sortable: false },
        { text: 'Attachment(s)', value: 'attachments', sortable: false },
        { text: 'Action', value: 'action', sortable: false, align: 'center' },
      ],
      filters: {
        term: null,
        month: null,
        session: null,
        name: null,
        name_cn: null,
        lesson_category_id: null,
      },
      lessonLoadings: {},
      addLessonDialog: false,
      editLessonDialog: {
        show: false,
        item: null,
        index: -1,
      },
    }
  },
  computed: {
    ...mapGetters({
      categoryList: 'lessonCategory/list',
      isCategoryListLoading: 'lessonCategory/isListLoading',
      curriculumList: 'curriculum/list',
      isCurriculumListLoading: 'curriculum/isListLoading',
    }),
    isReadonly() {
      return this.mode === 'view'
    },
    statusOptions() {
      return constant.lessonPlanStatusOptions
    },
    lessonCategories() {
      return this.categoryList.data
    },
    curriculums() {
      return this.curriculumList.data
    },
    lessons() {
      let collection = collect(this.form.lessons)
      if (this.filters.term) {
        collection = collection.where('term', this.filters.term)
      }
      if (this.filters.month) {
        collection = collection.where('month', this.filters.month)
      }
      if (this.filters.session) {
        collection = collection.where('session', this.filters.session)
      }
      if (this.filters.name) {
        const search = this.filters.name
        collection = collection.filter(
          (item) =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.name_cn.includes(search)
        )
      }
      if (this.filters.lesson_category_id) {
        collection = collection.where(
          'lesson_category_id',
          this.filters.lesson_category_id
        )
      }
      return collection.all()
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      upload: 'storage/upload',
      getCategoryList: 'lessonCategory/getList',
      getCurriculumList: 'curriculum/getList',
    }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.lessonPlan) {
        console.error(
          `lessonPlan props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    init() {
      if (this.mode != 'add' && this.lessonPlan != null) {
        _.merge(this.form, this.lessonPlan)
      }
      this.getCategoryList()
      this.getCurriculumList({
        status: 'ACTIVE',
        sort: ['rank,asc'],
      })
    },
    changeCurriculum(value) {
      const curriculum = this.curriculums.find((e) => e.id == value)
      if (curriculum) {
        this.form.rank = curriculum.rank
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    getLessonCategory(categoryId) {
      const category = this.lessonCategories.find((e) => e.id === categoryId)
      if (category) {
        return this.bilingualText(category, 'name', 'name_cn')
      }
      return null
    },
    showEditLessonDialog(lesson) {
      const idx = this.form.lessons.findIndex((e) => {
        if (lesson.id === null) {
          return (
            e.term === lesson.term &&
            e.month === lesson.month &&
            e.session == lesson.session &&
            e.name === lesson.name
          )
        } else {
          return e.id === lesson.id
        }
      })
      if (idx != -1) {
        this.editLessonDialog.item = _.cloneDeep(this.form.lessons[idx])
        this.editLessonDialog.index = idx
        this.editLessonDialog.show = true
      }
    },
    addLesson(lesson) {
      this.form.lessons.push(lesson)
      this.addLessonDialog = false
    },
    editLesson(lesson) {
      _.merge(this.form.lessons[this.editLessonDialog.index], lesson)
      this.editLessonDialog.index = -1
      this.editLessonDialog.item = null
      this.editLessonDialog.show = false
    },
    removeLesson(lesson) {
      const idx = this.form.lessons.findIndex((e) => {
        if (lesson.id === null) {
          return (
            e.term === lesson.term &&
            e.month === lesson.month &&
            e.session == lesson.session &&
            e.name === lesson.name
          )
        }
        return e.id === lesson.id
      })
      if (idx != -1) {
        this.form.lessons.splice(idx, 1)
      }
    },
    async submit() {
      if (this.$refs.form.validate()) {
        if (this.mode === 'add') {
          this.form.post('/lesson-plans')
        } else if (this.mode === 'edit') {
          this.form.put(`/lesson-plans/${this.lessonPlan.id}`)
        }
      }
    },
  },
}
</script>
